import React from 'react';
import { ResponsivePie } from '@nivo/pie';
import { curr } from 'helpers/curr';
import { DashboardCurrency } from '../dashboardFilters.machine';

interface ChartLiquidityProps {
  data: {
    aggr_balance_pct: number;
    name: string;
    aggr_balance_gbp: number;
    aggr_balance_eur: number;
    aggr_balance_usd: number;
    lower_limit_pct: number;
    upper_limit_pct: number;
    shouldDisplayAsRed: boolean;
  }[];
  currency: DashboardCurrency;
}

const ChartLiquidity = ({ data, currency }: ChartLiquidityProps) => {
  let key: 'aggr_balance_gbp' | 'aggr_balance_usd' | 'aggr_balance_eur' =
    'aggr_balance_gbp';

  if (currency === 'EUR') {
    key = 'aggr_balance_eur';
  } else if (currency === 'USD') {
    key = 'aggr_balance_usd';
  }

  return (
    <div className="flex flex-col flex-shrink-0 bg-white border border-gray-400 rounded-lg shadow-sm w-96">
      <h2 className="flex items-start justify-between p-5 mb-5 text-xs font-semibold tracking-wider text-gray-700 uppercase bg-gray-100 border-b border-gray-200 rounded-t-lg">
        <span className="mr-5">Liquidity</span>
      </h2>
      <div className="flex flex-col h-full">
        <div className="flex-shrink-0 w-full h-48">
          <ResponsivePie
            data={data
              .filter((item) => Math.round(item.aggr_balance_pct) > 1)
              .map((item, index) => ({
                id: item.name,
                label: item.name,
                value: Math.round(item.aggr_balance_pct),
                amount: item[key],
                shouldDisplayAsRed: item.shouldDisplayAsRed as any,
                index,
              }))}
            innerRadius={0.5}
            padAngle={1}
            cornerRadius={3}
            colors={(data) => {
              if (data.shouldDisplayAsRed) {
                return (
                  ['#F56565', '#E53E3E', '#C53030', '#9B2C2C', '#742A2A'][
                    data.index
                  ] || '#742A2A'
                );
              }
              return (
                [
                  '#63B3ED',
                  '#4299E1',
                  '#3182CE',
                  '#2B6CB0',
                  '#2C5282',
                  '#2A4365',
                ][data.index] || '#2A4365'
              );
            }}
            theme={{ fontFamily: 'Inter' } as any}
            enableRadialLabels={false}
            // radialLabel={(e) => e.id.toString()}
            // radialLabelsLinkDiagonalLength={10}
            // radialLabelsLinkHorizontalLength={5}
            // radialLabelsLinkColor="#fff"
            enableSlicesLabels={true}
            sliceLabel={(e) => e.value.toString() + '%'}
            slicesLabelsTextColor="#fff"
            animate={true}
            motionStiffness={90}
            motionDamping={15}
            margin={{ left: 0, right: 140, top: 0, bottom: 5 }}
            tooltip={(props) => (
              <div className="px-2 py-1 -m-3 text-xs text-white rounded bg-jcap-700">
                <p>{props?.id}</p>
                <p>{curr(Number(props?.amount), currency)}</p>
              </div>
            )}
            legends={[
              {
                anchor: 'right',
                direction: 'column',
                itemsSpacing: 12,
                itemWidth: 16,
                itemHeight: 16,
                itemTextColor: '#666',
                symbolSize: 18,
                symbolShape: 'circle',
                padding: { right: -40 },
                effects: [
                  {
                    on: 'hover',
                    style: {
                      itemTextColor: '#000',
                    },
                  },
                ],
              },
            ]}
          />
        </div>
        <div className="pt-3 pb-4 text-sm text-center text-gray-600">
          {curr(
            data?.find((item) => item.name === 'CALL')?.[key] || 0,
            currency,
          )}{' '}
          available in CALL accounts
          {data.map(
            (bracket) =>
              bracket?.shouldDisplayAsRed === true &&
              bracket?.[key] > 0 && (
                <div
                  className="pt-2 text-sm text-center text-red-700"
                  key={bracket.name}
                >
                  {Math.round(bracket?.aggr_balance_pct)}% in {bracket?.name}{' '}
                  deposits: LIMIT {bracket?.lower_limit_pct}-
                  {bracket?.upper_limit_pct}%
                </div>
              ),
          )}
        </div>
      </div>
    </div>
  );
};

export default ChartLiquidity;
